import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, ArrowRight, AlertCircle, MapPin } from 'lucide-react';

// --- CONFIG ---
const VALID_LOGIN = 'Motivya2026';
const VALID_PASSWORD = 'Pass4motiv!';
const STORAGE_KEY = 'motivya_prelaunch_auth';

// Check if launch mode is enabled (pre-launch/coming-soon page)
// Set REACT_APP_LAUNCH_MODE=false in .env to disable the coming soon page
export const isLaunchModeEnabled = () => {
  const launchMode = process.env.REACT_APP_LAUNCH_MODE;
  // Default to true (coming soon enabled) if not set
  return launchMode !== 'false';
};

export function isPrelaunchAuthenticated() {
  // If launch mode is disabled, always return true (skip coming soon)
  if (!isLaunchModeEnabled()) {
    return true;
  }
  return localStorage.getItem(STORAGE_KEY) === 'true';
}

export function clearPrelaunchAuth() {
  localStorage.removeItem(STORAGE_KEY);
}

// --- Animated Background Particles ---
function ParticleField() {
  const particles = Array.from({ length: 40 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 3 + 1,
    duration: Math.random() * 20 + 15,
    delay: Math.random() * 10,
    opacity: Math.random() * 0.3 + 0.1,
  }));

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      {particles.map(p => (
        <motion.div
          key={p.id}
          className="absolute rounded-full"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            background: `rgba(59, 173, 229, ${p.opacity})`,
          }}
          animate={{
            y: [0, -60, 0],
            x: [0, Math.random() * 30 - 15, 0],
            opacity: [p.opacity, p.opacity * 2, p.opacity],
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
}

// --- Main Component ---
export default function ComingSoonPage({ onAuthenticated }) {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [pageReady, setPageReady] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setPageReady(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    setTimeout(() => {
      if (login === VALID_LOGIN && password === VALID_PASSWORD) {
        localStorage.setItem(STORAGE_KEY, 'true');
        onAuthenticated();
      } else {
        setError('Identifiants incorrects. Veuillez réessayer.');
        setIsLoading(false);
      }
    }, 600);
  }, [login, password, onAuthenticated]);

  return (
    <AnimatePresence>
      {pageReady && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2 }}
          className="min-h-screen flex flex-col relative overflow-hidden"
          style={{ background: 'linear-gradient(160deg, #0a0f1a 0%, #0d1526 40%, #0f1a2e 70%, #0a1420 100%)' }}
        >
          <ParticleField />

          {/* Ambient glow */}
          <div className="fixed top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(59,173,229,0.06) 0%, transparent 70%)' }}
          />
          <div className="fixed bottom-0 right-0 w-[400px] h-[400px] rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(34,197,94,0.04) 0%, transparent 70%)' }}
          />

          {/* Content */}
          <div className="flex-1 flex flex-col items-center justify-center px-4 relative z-10">

            {/* Animated Logo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.6, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="mb-8"
            >
              <motion.div
                animate={{
                  filter: [
                    'drop-shadow(0 0 15px rgba(59,173,229,0.2))',
                    'drop-shadow(0 0 40px rgba(59,173,229,0.5))',
                    'drop-shadow(0 0 15px rgba(59,173,229,0.2))',
                  ],
                }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              >
                <img
                  src="/logo.png"
                  alt="Motivya"
                  className="h-24 sm:h-32 md:h-40 w-auto"
                  data-testid="coming-soon-logo"
                />
              </motion.div>
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="text-3xl sm:text-4xl md:text-5xl font-black text-white text-center tracking-tight mb-4"
              style={{ fontFamily: "'Bebas Neue', 'Oswald', sans-serif", letterSpacing: '0.02em' }}
            >
              Motivya arrive bientôt
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="text-base sm:text-lg text-[#3BADE5] font-medium text-center max-w-lg mb-3"
            >
              Préparez-vous à découvrir la révolution du sport dans votre quartier.
            </motion.p>

            {/* Body text */}
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="text-sm sm:text-base text-white/50 text-center max-w-md mb-10 leading-relaxed"
            >
              Des séances accessibles, proches de chez vous, avec des coachs motivants.
              Motivya transforme l'envie de bouger en action.
            </motion.p>

            {/* Login Form */}
            <motion.form
              onSubmit={handleSubmit}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="w-full max-w-sm"
            >
              <div className="relative rounded-2xl p-[1px] overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(59,173,229,0.3), rgba(34,197,94,0.2), rgba(59,173,229,0.1))' }}>
                <div className="rounded-2xl bg-[#0d1526]/95 backdrop-blur-xl p-6 space-y-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Lock className="w-4 h-4 text-[#3BADE5]" />
                    <span className="text-xs text-white/40 uppercase tracking-widest font-medium">Accès restreint</span>
                  </div>

                  <div>
                    <input
                      type="text"
                      placeholder="Identifiant"
                      value={login}
                      onChange={e => { setLogin(e.target.value); setError(''); }}
                      data-testid="prelaunch-login"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder:text-white/25 focus:outline-none focus:border-[#3BADE5]/50 focus:ring-1 focus:ring-[#3BADE5]/20 transition-all"
                      autoComplete="username"
                    />
                  </div>

                  <div>
                    <input
                      type="password"
                      placeholder="Mot de passe"
                      value={password}
                      onChange={e => { setPassword(e.target.value); setError(''); }}
                      data-testid="prelaunch-password"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder:text-white/25 focus:outline-none focus:border-[#3BADE5]/50 focus:ring-1 focus:ring-[#3BADE5]/20 transition-all"
                      autoComplete="current-password"
                    />
                  </div>

                  <AnimatePresence>
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="flex items-center gap-2 text-red-400 text-xs"
                      >
                        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>{error}</span>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <motion.button
                    type="submit"
                    disabled={isLoading || !login || !password}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid="prelaunch-submit"
                    className="w-full py-3 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{
                      background: 'linear-gradient(135deg, #3BADE5 0%, #22c55e 100%)',
                      color: 'white',
                    }}
                  >
                    {isLoading ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                      />
                    ) : (
                      <>
                        Entrer
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </motion.button>
                </div>
              </div>
            </motion.form>

            {/* Secondary block */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.6 }}
              className="mt-10 text-center max-w-md"
            >
              <p className="text-xs text-white/30 leading-relaxed">
                Le lancement approche. Restez connectés pour découvrir une nouvelle façon
                de réserver vos activités sportives locales.
              </p>
            </motion.div>
          </div>

          {/* Footer */}
          <motion.footer
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 2 }}
            className="relative z-10 py-6 text-center"
          >
            <div className="flex items-center justify-center gap-1.5 text-white/20 text-xs">
              <span>&copy; Motivya</span>
              <span className="mx-1">·</span>
              <MapPin className="w-3 h-3" />
              <span>Bruxelles</span>
            </div>
          </motion.footer>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
